import { Clear, Search } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { addMonths, endOfMonth, format, startOfMonth, subDays, subMonths, subYears } from "date-fns";
import { useEffect, useState } from "react";
import { clientService } from "../../../shared/services/api/client/clientService";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";

export interface ToolbarNFSEProps {
	textSearch: string;
	onChangeTextSearch: (value: string) => void;
	onClearTextSearch: () => void;
	dataIni: string;
	dataFim: string;
	onChangeDateStart: (value: string) => void;
	onChangeDateEnd: (value: string) => void;
	status: string;
	onChangeStatus: (value: string) => void;
	partnerId: string;
	onChangePartner: (value: string) => void;
	onClickRefresh: () => void;
}

export const ToolbarNFSE = ({
	textSearch,
	onChangeTextSearch,
	onClearTextSearch,
	dataIni,
	dataFim,
	onChangeDateStart,
	onChangeDateEnd,
	status,
	onChangeStatus,
	partnerId,
	onChangePartner,
	onClickRefresh,
}: ToolbarNFSEProps) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [periodType, setPeriodType] = useState("1_mes");
	const [clientOptionsList, setClientOptionsList] = useState<{ id: string; label: string }[]>([]);
	const [clientSearchText, setClientSearchText] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsLoading(true);
		clientService.getSelect(1, clientSearchText?.replace("(Inativo)", "")?.trim(), "", "", "").then((result) => {
			setIsLoading(false);
			if (!(result instanceof Error)) {
				setClientOptionsList(
					result.data.map((client: any) => ({
						id: client.id,
						label: client.status === "inactive" ? `${client.name} (Inativo)` : client.name,
					}))
				);
			}
		});
	}, [clientSearchText]);

	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start: Date = now;
		let end: Date = now;

		switch (period) {
			case "15_dias":
				start = subDays(now, 15);
				end = now;
				break;
			case "1_mes":
				start = startOfMonth(now);
				end = endOfMonth(now);
				break;
			case "2_meses":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(now);
				break;
			case "3_meses":
				start = startOfMonth(subMonths(now, 2));
				end = endOfMonth(now);
				break;
			case "1_ano":
				start = startOfMonth(subYears(now, 1));
				end = endOfMonth(now);
				break;
			case "2_ano":
				start = startOfMonth(subYears(now, 2));
				end = endOfMonth(now);
				break;
			case "mes_anterior":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(subMonths(now, 1));
				break;
			case "proximo_mes":
				start = startOfMonth(addMonths(now, 1));
				end = endOfMonth(addMonths(now, 1));
				break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);
		if (value !== "personalizado") {
			const [start, end] = getDateRangeFromPeriod(value);
			onChangeDateStart(start);
			onChangeDateEnd(end);
		}
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems="center">
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="period-label">Período</InputLabel>
						<Select labelId="period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
							<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
							<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
							<MenuItem value="1_mes">Mês Atual</MenuItem>
							<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
							<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
							<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
							<MenuItem value="1_ano">Último Ano</MenuItem>
							<MenuItem value="2_ano">Últimos 2 anos</MenuItem>
							<MenuItem value="personalizado">Personalizado</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				{periodType === "personalizado" && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data Inicial"
								value={dataIni}
								onChange={(e) => onChangeDateStart(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data Final"
								value={dataFim}
								onChange={(e) => onChangeDateEnd(e.target.value)}
							/>
						</Grid>
					</>
				)}
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<Autocomplete
							options={clientOptionsList}
							getOptionLabel={(option) => option.label}
							value={clientOptionsList.find((opt) => opt.id === partnerId) || null}
							loading={isLoading}
							onInputChange={(_, newInputValue) => setClientSearchText(newInputValue)}
							onChange={(_, value) => onChangePartner(value?.id || "")}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Parceiro"
									size="small"
									fullWidth
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{isLoading ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</FormControl>
				</Grid>

				{/* STATUS */}
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="status-label">Status NFSE</InputLabel>
						<Select labelId="status-label" value={status} label="Status NFSE" onChange={(e) => onChangeStatus(e.target.value)}>
							<MenuItem value="todos">Todos</MenuItem>
							<MenuItem value="authorized">Autorizada</MenuItem>
							<MenuItem value="canceled">Cancelada</MenuItem>
							<MenuItem value="unauthorized">Nao Autorizada</MenuItem>
							<MenuItem value="incancellation">Em cancelamento</MenuItem>
							<MenuItem value="unprocessed">Nao processada</MenuItem>
							<MenuItem value="paid">A emitir</MenuItem>
							<MenuItem value="issued">Emitida</MenuItem>
							<MenuItem value="rejected">Rejeitada</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						size="small"
						placeholder="Pesquisar..."
						value={textSearch}
						onChange={(e) => onChangeTextSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={() => {
											onClearTextSearch();
											onChangeTextSearch("");
										}}
									>
										<Clear />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={12} md>
					<Box display="flex" justifyContent="flex-end" gap={1}>
						<Button variant="contained" onClick={onClickRefresh}>
							Pesquisar
						</Button>
					</Box>
				</Grid>
			</Grid>

			<Typography variant="body2" sx={{ mt: 1, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
				Período: {formatDateFnsRemoveTimezone(dataIni)} - {formatDateFnsRemoveTimezone(dataFim)}
			</Typography>
		</Box>
	);
};
